import angular from 'angular';
import common from './common/video-chat.common';
import loadingScreen from './loading-screen/video-chat.loading-screen';
import callActions from './call-actions/call-actions';
import callFrames from './call-frames/call-frames';
import callConfirmationPopup from './call-confirmation-popup/call-confirmation-popup';
import userMediaStatus from './user-media-status/user-media-status';
import videoStillBackground from './video-still-image-background';
import sharingIndicator from './call-frames/elements/sharing-indicator/sharing-indicator';
import to from 'await-to-js';
import videoChatRealtimeComponent from './video-chat.realtime-component';
import template from './video-chat.html';
import './video-chat.styles/video-chat.scss';
import * as devices from '../modules/devices';
import * as core from '../modules/core';
import * as call from '../modules/call';
import { getIsCaptionsOpen, toggleClosedCaptionsOpen, } from '../modules/captions';
import { RootScopeEvents } from '../common/services/root-scope-types';
import { PublisherMediaWatcher, } from '../providers/video/tokbox/publisher-media-watcher.class';
import { AutoMediaDisabler } from '../providers/video/common/services/auto-media-disabler';
import confirmLeaveModal from './confirm-leave-modal/confirm-leave-modal.js';
import { disableAutoLockIfItCausesIssues, keepBrowserActive, } from '../modules/webrtc';
import backgroundEffects from '../modules/call/angular-integrations/background-effects';
import { isCheckInPhotoCaptureEnabled } from '../modules/settings/helpers';
export default angular
    .module('doxy.videoChat', [
    common,
    loadingScreen,
    callActions,
    callConfirmationPopup,
    callFrames,
    userMediaStatus,
    videoStillBackground,
    videoChatRealtimeComponent,
    sharingIndicator,
    confirmLeaveModal,
    backgroundEffects,
])
    .run((VideoProviderFactory, $injector, $rootScope) => {
    const publisherMediaWatcher = new PublisherMediaWatcher($injector);
    publisherMediaWatcher.on('error', (data) => {
        $rootScope.$emit(RootScopeEvents.MEDIA_BROKEN, data);
    });
    publisherMediaWatcher.start();
    const autoMediaDisabler = new AutoMediaDisabler(VideoProviderFactory, $rootScope);
    autoMediaDisabler.start();
    keepBrowserActive($rootScope);
})
    .component('videoChat', {
    template,
    bindings: {
        isPatient: '<',
        room: '<',
        currentInstitution: '<',
    },
    controller: function ($rootScope, $timeout, $scope, $location, Browser, globalUi, activeComponents, VideoProviderFactory, enableCameraErrorHandler, callQualitySurveyService, $ngRedux) {
        // store related
        this.mapStateToThis = (state) => {
            return {
                hasMediaAccess: devices.selectors.hasMediaAccess(state),
                hasMicrophoneAccess: devices.selectors.hasMicrophoneAccess(state),
                isModalDisableCameraOpen: devices.selectors.isModalDisableCameraOpen(state),
                isBackgroundEffectsModalOpen: core.selectors.isBackgroundEffectsModalOpen(state),
                isClosedCaptionsOpen: getIsCaptionsOpen(state),
            };
        };
        this.onStoreUpdate = () => {
            const state = $ngRedux.getState();
            this.checkDevicesChange(state);
        };
        this.enableWebcam = async () => {
            const delay = Browser.isMobileSafari() ? 2000 : 0;
            await $timeout(async () => {
                const [err] = await to(VideoProviderFactory.activeProvider.enableWebcam());
                if (err) {
                    enableCameraErrorHandler.onCommon(err);
                }
            }, delay);
        };
        const updateRouteWithoutCamera = () => {
            if (Boolean(this.isPatient)) {
                return;
            }
            this.routeWithoutCamera = $location.path() !== '/account/dashboard';
        };
        const enableWebcam = async () => {
            this.cameraOff =
                (!Boolean(this.isPatient) &&
                    Boolean(this.room) &&
                    Boolean(this.room.cameraOff)) ||
                    Boolean(this.routeWithoutCamera);
            if (!Boolean(this.hasMediaAccess) || Boolean(this.cameraOff)) {
                return;
            }
            await this.enableWebcam();
        };
        this.checkDevicesChange = (state) => {
            const deviceHash = devices.selectors.getDevicesHash(state);
            if (this.deviceHash !== deviceHash) {
                // not first time
                if (this.deviceHash !== undefined) {
                    this.onDevicesChange();
                }
                this.deviceHash = deviceHash;
            }
        };
        this.onDevicesChange = () => {
            if (VideoProviderFactory.activeProvider.isPublisherLoading) {
                return;
            }
            const publisher = VideoProviderFactory.activeProvider.getPublisher();
            if (!Boolean(publisher) || !Boolean(publisher.videoElement())) {
                void enableWebcam();
                return;
            }
            const videoSource = publisher.getVideoSource();
            const audioSource = publisher.getAudioSource();
            // when camera enabled after the microphone or mic after camera
            if (!Boolean(videoSource) ||
                !videoSource.deviceId ||
                !Boolean(audioSource)) {
                void enableWebcam();
            }
        };
        this.unsubscribeFromStore = $ngRedux.connect(this.mapStateToThis)(this);
        this.unsubscribeFromStoreUpdate = $ngRedux.subscribe(() => this.onStoreUpdate());
        // /store related
        this.callQualitySurveyService = callQualitySurveyService;
        VideoProviderFactory.decorateScope(this);
        this.ui = globalUi.ui;
        this.activeComponents = activeComponents;
        this.hidePublisher = false;
        this.mobileGalleryWasDisabled = false;
        this.$onInit = () => {
            updateRouteWithoutCamera();
            $rootScope.$on('$locationChangeSuccess', updateRouteWithoutCamera);
            void enableWebcam();
            disableAutoLockIfItCausesIssues();
        };
        $scope.$watch('$ctrl.routeWithoutCamera', async () => {
            if (Boolean(this.isPatient)) {
                return;
            }
            const provider = VideoProviderFactory.activeProvider;
            // Wait for any in-progress initialization to complete before taking action
            if (provider.isPublisherLoading) {
                const maxWait = 3000;
                const startTime = Date.now();
                while (provider.isPublisherLoading && Date.now() - startTime < maxWait) {
                    await new Promise((resolve) => setTimeout(resolve, 100));
                }
            }
            // Combined conditions: check route and loading state together
            if (!provider.isPublisherLoading && this.routeWithoutCamera === false) {
                void enableWebcam();
            }
            else if (!provider.isPublisherLoading && this.routeWithoutCamera !== false) {
                await provider.destroyPublishers();
            }
        });
        $scope.$watch('$ctrl.ui.video', () => {
            if (!Boolean(this.cameraOff) || !Boolean(this.hasMediaAccess)) {
                return;
            }
            if (Boolean(this.ui.video)) {
                void $timeout(() => this.enableWebcam());
            }
            else {
                void VideoProviderFactory.activeProvider.destroyPublishers();
            }
        });
        this.toggleVideo = () => {
            if (Boolean(this.hasMediaAccess) &&
                !Boolean(this.patientModalOpen) &&
                !Browser.isMobileScreen() &&
                Browser.isSupported()) {
                this.hidePublisher = !Boolean(this.hidePublisher);
            }
        };
        this.shouldShowPublisher = () => {
            return (Boolean(VideoProviderFactory.activeProvider) &&
                (!Boolean(this.hasMediaAccess) ||
                    Boolean(this.ui.video) ||
                    !Boolean(this.cameraOff)));
        };
        this.getPublisherVisibility = () => {
            // Note: there might be a css rule applied that hides the publisher on "md"-ish resolutions
            if (Browser.isMobileScreen()) {
                return 'visible';
            }
            return (Boolean(this.ui.checkIn) && Boolean(this.cameraOff)) ||
                Boolean(this.hidePublisher)
                ? 'hidden'
                : 'visible';
        };
        this.onToggleGalleryView = (status, userToggle = false) => {
            if (userToggle) {
                $rootScope.$emit(Boolean(status)
                    ? RootScopeEvents.USER_ACTIONS.GALLERY_VIEW_ENABLED
                    : RootScopeEvents.USER_ACTIONS.DEFAULT_VIEW_ENABLED);
            }
            this.ui.isGalleryViewActivated = status;
            $rootScope.$emit('galleryViewStatusChanged', status);
        };
        this.isSharingIndicatorVisible = () => Boolean(this.ui.screenSharing) && !Boolean(this.ui.isShowingAdherely);
        this.stopSharing = () => {
            console.warn('[video-chat] : Stop sharing');
            this.ui.screenSharing = false;
        };
        this.isCheckInPhotoCaptureEnabled = isCheckInPhotoCaptureEnabled({
            room: this.room,
            institution: this.currentInstitution,
        });
        this.onCloseCaptions = () => {
            $ngRedux.dispatch(toggleClosedCaptionsOpen(false, this.isPatient));
        };
        $rootScope.$on('patient.modal.hide.before', () => {
            this.patientModalOpen = false;
        });
        $rootScope.$on('patient.modal.show.box', () => {
            this.patientModalOpen = true;
        });
        $rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.END_REASON, () => {
            $ngRedux.dispatch(call.actions.cleanupCaptionsOnCallEnd());
        });
        window.addEventListener('resize', () => {
            if (!Boolean(this.ui.isGalleryViewActivated) &&
                !Boolean(this.mobileGalleryWasDisabled)) {
                return;
            }
            if (Browser.isMobileWindowWidth) {
                this.mobileGalleryWasDisabled = true;
                this.onToggleGalleryView(false);
            }
            else {
                this.mobileGalleryWasDisabled = false;
                this.onToggleGalleryView(true);
            }
            $scope.$digest();
        });
        $scope.$on('$destroy', () => {
            this.unsubscribeFromStore();
            this.unsubscribeFromStoreUpdate();
        });
    },
}).name;
